import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * COI-819 — grant/revoke a RiskSmart-internal user's support entitlement.
 *
 * tRPC-only: `createMutationHook` requires a `graphqlDocument` and this write
 * has no Apollo twin to bridge to. V2 administered support access through the
 * single-role dropdown, which V3 replaced with the custom-role picker — there is
 * no legacy mutation for the same write, so the flag-gated wrapper shape does
 * not apply (see the "mutation with no GraphQL twin" exception in
 * `packages/web/CLAUDE.md`).
 *
 * The tradeoff that exception carries is that the hook is live regardless of the
 * `trpc` flag, so the feature must be unreachable with the flag off by some
 * other means. Here it is doubly so: the only caller renders behind BOTH the
 * multi-role (`trpc`) branch of `UserDetailsModal` and `useIsCustomerSupport`,
 * and the procedure itself is gated server-side by `assertCustomerSupport`.
 */
export const useUpdateSupportAccess = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation(
    trpc.frontend.user.updateSupportAccess.mutationOptions({
      // Same families `useUpdateCustomRoles` invalidates: the settings table and
      // both per-user read paths render role state that this write changes.
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.user.users.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.user.userById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.user.userByIdWithRoles.queryKey(),
          }),
        ]);
      },
    })
  );
};
